import styled from '@emotion/styled';
import type { InputLabelProps, TextFieldProps } from '@mui/material';
import { InputLabel } from '@mui/material';
import { inputBaseClasses, inputClasses, inputLabelClasses, TextField } from '@mui/material';
import { isNotEmptyString } from '@vy/utils';
import React from 'react';
import { VyHint } from '../../new-design/data-display/VyHint';

const ForwardedTextField = React.forwardRef<HTMLInputElement, TextFieldProps<'standard'> & { hint?: string }>(
  ({ hint, ...props }, ref) => (
    <TextField<'standard'>
      ref={ref}
      autoComplete="off"
      {...props}
      variant="standard"
      slots={{
        inputLabel: (inputLabelProps: InputLabelProps) => (
          <div className="relative mb-4">
            <InputLabel {...inputLabelProps} />
            {isNotEmptyString(hint) && (
              <div className="absolute justify-end right-0 top-[-10px]">
                <VyHint tooltipContent={hint} />
              </div>
            )}
          </div>
        ),
      }}
      slotProps={{
        input: {
          disableUnderline: true,
          onWheel: (event) => {
            event.currentTarget.querySelector('input')?.blur();
          },
          ...props.slotProps?.input,
        },

        select: {
          MenuProps: {
            slotProps: {
              paper: {
                sx: {
                  marginTop: '8px',
                  padding: 0,
                  boxShadow: '0px 8px 16px 0px #0C124514',
                  border: '1px solid #F0F4F6',
                },
              },
            },
          },
        },

        inputLabel: {
          shrink: true,
          ...props.slotProps?.inputLabel,
        } as InputLabelProps
      }} />
  ),
);

export const VayuTextField = styled(ForwardedTextField)({
  [`& .${inputBaseClasses.input}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    background: 'transparent',
    alignItems: 'flex-end',
  },
  [`& .${inputClasses.root}`]: {
    transitionDuration: '0.2s',
    transitionTimingFunction: 'linear',
    '&.Mui-focused': {
      position: 'static',
      transitionDuration: '0.1s',
      transitionTimingFunction: 'linear',
      background: '#F6F6F6',
      padding: '8px 26px',
      borderRadius: '10px',
      '&.Mui-error': {
        border: '1px solid #FF154D',
      },
    },
  },
  [`& .${inputClasses.multiline}`]: {
    '&.Mui-focused': {
      padding: '12px 26px',
    },
  },
  [`& .${inputLabelClasses.root}`]: {
    fontSize: '18px',
  },
  [`& .${inputLabelClasses.shrink}`]: {
    transform: 'translate(0, -12px) scale(0.8)',
  },
  [`& .${inputLabelClasses.root}.Mui-error`]: {
    color: '#6D6D6D',
  },
  '& label.Mui-focused': {
    color: '#6D6D6D',
  },
  '& .MuiAutocomplete-endAdornment': {
    marginTop: '8px',
  },
});
